import React from 'react';
import { useCookies } from 'react-cookie';

import { useAcceptPrivacyTermsMutation } from 'generated/graphql';

import UserConsentInput from 'components/Common/UserConsentInput';
import { ComponentProps } from 'components/Common/UserConsentInput/UserConsentInput';

export type UserConsentCookie = {
	userConsent: boolean;
};

export type UserConsentHookResponse = {
	userConsent: boolean;
	accepted: boolean;
	inputError: boolean;
	renderInput(renderProps?: ComponentProps['renderProps']): React.ReactNode;
	validate(): boolean;
	changeConsent(value: boolean): void;
	onSubmit(submitFn: Function): void;
	acceptConsent(email: string): Promise<void>;
};

type UserConsentHook = () => UserConsentHookResponse;

const COOKIE_NAME = 'privacy-user-consent';
const COOKIE_EXPIRATION = 31536000;

export const useUserConsent: UserConsentHook = () => {
	const [cookies, setCookie] = useCookies();
	const cookie: UserConsentCookie = cookies[COOKIE_NAME];

	const [acceptPrivacyTerms] = useAcceptPrivacyTermsMutation();

	const accepted = cookie?.userConsent || false;
	const [userConsent, setUserConsent] = React.useState(() => accepted);
	const [inputError, setInputError] = React.useState(false);
	React.useEffect(() => {
		setUserConsent(cookie?.userConsent || false);
	}, [cookie]);

	const changeConsent = React.useCallback((value: boolean) => {
		setInputError(false);
		setUserConsent(value);
	}, []);

	const validate = React.useCallback(() => {
		setInputError(!userConsent);
		return userConsent;
	}, [userConsent]);

	const onSubmit = React.useCallback(
		(submitFn: Function) => {
			validate() && submitFn();
		},
		[validate]
	);

	const acceptConsent = React.useCallback(
		async (email: string) => {
			if (accepted) return;
			await acceptPrivacyTerms({
				variables: {
					privacy: {
						email
					}
				}
			});
			const newCookie = { ...cookie, userConsent: true };
			setCookie(COOKIE_NAME, newCookie, { maxAge: COOKIE_EXPIRATION });
		},
		[acceptPrivacyTerms, accepted, cookie, setCookie]
	);

	const renderInput = React.useCallback(
		(renderProps?: ComponentProps['renderProps']) => {
			return (
				!accepted && (
					<UserConsentInput checked={userConsent} onConsentChange={changeConsent} inputError={inputError} renderProps={renderProps} />
				)
			);
		},
		[accepted, changeConsent, inputError, userConsent]
	);

	return {
		userConsent,
		accepted,
		inputError,
		changeConsent,
		onSubmit,
		acceptConsent,
		validate,
		renderInput
	};
};
